import { useEffect, useState } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { getServiceCategoryById, updateServiceCategory } from "@/services/supabaseService";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import ReactQuill from "react-quill-new";
import * as lucideIcons from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import "react-quill-new/dist/quill.snow.css";
import ImageUpload from "../../../../components/common/ImageUpload";
import { uploadToS3 } from "../../../../utils/s3Uploader";

const languageFlags = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ar: "🇸🇦"
};

function IconSearchSelect({ value, onChange }) {
  const [search, setSearch] = useState("");
  const iconNames = Object.keys(lucideIcons).filter((name) => name[0] === name[0].toUpperCase());
  const filtered = iconNames.filter((name) => name.toLowerCase().includes(search.toLowerCase()));
  return (
    <div>
      <Input placeholder="İkon ara... (örn: Smile, Star)" value={search} onChange={e => setSearch(e.target.value)} />
      <div className="max-h-48 overflow-auto border rounded mt-2 bg-background z-10">
        {filtered.slice(0, 30).map((name) => {
          const Icon = lucideIcons[name];
          return (
            <div key={name} className={`flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-accent ${value === name ? "bg-secondary" : ""}`} onClick={() => onChange(name)}>
              <Icon className="h-5 w-5" />
              <span className="text-foreground">{name}</span>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="px-2 py-1 text-muted-foreground">Sonuç yok</div>}
      </div>
    </div>
  );
}

export default function EditServiceCategory() {
  const { id } = useParams<{ id: string }>();
  const [name, setName] = useState<Record<string, string>>({ tr: "", en: "", ar: "" });
  const [description, setDescription] = useState<Record<string, string>>({ tr: "", en: "", ar: "" });
  const [currentImageUrl, setCurrentImageUrl] = useState<string>("");
  const [image, setImage] = useState<File | null>(null);
  const [icon, setIcon] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchCategory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCategory = async () => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      const category = await getServiceCategoryById(id);
      
      if (category) {
        let nameObj = { tr: "", en: "", ar: "" };
        if (category.name) {
          try {
            nameObj = typeof category.name === 'string' ? JSON.parse(category.name) : category.name;
          } catch (e) {
            nameObj = { tr: String(category.name) || "", en: "", ar: "" };
          }
        }
        
        let descObj = { tr: "", en: "", ar: "" };
        if (category.description) {
          try {
            descObj = typeof category.description === 'string' ? JSON.parse(category.description) : category.description;
          } catch (e) {
            descObj = { tr: String(category.description) || "", en: "", ar: "" };
          }
        }
        
        const nameValues = {
          tr: nameObj.tr || "",
          en: nameObj.en || "",
          ar: nameObj.ar || ""
        };
        
        const descValues = {
          tr: descObj.tr || "",
          en: descObj.en || "",
          ar: descObj.ar || ""
        };
        
        setName(nameValues);
        setDescription(descValues);
        setCurrentImageUrl(category.image_url || "");
        setIcon(category.icon || "");
      } else {
        toast({
          title: "Hata",
          description: "Kategori bulunamadı.",
          variant: "destructive",
        });
        navigate("/content/services/categories");
      }
    } catch (error) {
      console.error("Kategori yükleme hatası:", error);
      toast({
        title: "Hata",
        description: "Kategori yüklenirken bir hata oluştu.",
        variant: "destructive",
      });
      navigate("/content/services/categories");
    } finally {
      setIsLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!currentImageUrl && !image) {
        toast({
          title: "Hata",
          description: "Lütfen bir resim seçin.",
          variant: "destructive",
        });
        return;
      }
      
      if (!icon) {
        toast({
          title: "Hata",
          description: "Lütfen bir ikon seçin",
          variant: "destructive",
        });
        return;
      }
      if (!name.tr.trim()) {
        toast({
          title: "Hata",
          description: "Lütfen Türkçe kategori adını girin.",
          variant: "destructive",
        });
        return;
      }
      
      setIsSubmitting(true);
      let imageUrl = currentImageUrl || "";
      
      if (image) {
        try {
          const uploadResult = await uploadToS3(image, "servicesCategories");
          if (uploadResult.url) {
            imageUrl = uploadResult.url;
          } else {
            throw new Error("Resim yükleme başarısız");
          }
        } catch (uploadError) {
          console.error("Resim yükleme hatası:", uploadError);
          toast({
            title: "Hata",
            description: "Resim yüklenirken bir hata oluştu",
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }
      }
      
      if (!id) {
        throw new Error("Kategori ID'si bulunamadı");
      }
      
      const nameJson = JSON.stringify(name);
      const descriptionJson = JSON.stringify(description);
      
      const result = await updateServiceCategory(
        id,
        nameJson,
        icon,
        descriptionJson,
        imageUrl || ""
      );
      
      if (result) {
        toast({
          title: "Başarılı",
          description: "Kategori başarıyla güncellendi.",
        });
        navigate("/content/services/categories");
      } else {
        throw new Error("Kategori güncellenemedi");
      }
    } catch (error) {
      console.error("Form gönderme hatası:", error);
      toast({
        title: "Hata",
        description: "Kategori güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (image || name.tr !== '' || name.en !== '' || name.ar !== '' || description.tr !== '' || description.en !== '' || description.ar !== '' || icon) {
      if (window.confirm('Değişiklikleriniz kaydedilmeyecek. Çıkmak istediğinizden emin misiniz?')) {
        navigate('/content/services/categories');
      }
    } else {
      navigate('/content/services/categories');
    }
  };

  if (isLoading) {
    return (
      <ContentLayout 
        title="Kategori Düzenle" 
        backLink="/content/services/categories"
      >
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout 
      title="Kategori Düzenle" 
      backLink="/content/services/categories"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Tabs defaultValue="tr" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="tr">{languageFlags.tr} Türkçe</TabsTrigger>
                <TabsTrigger value="en">{languageFlags.en} English</TabsTrigger>
                <TabsTrigger value="ar">{languageFlags.ar} العربية</TabsTrigger>
              </TabsList>
              <TabsContent value="tr" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name-tr">Kategori Adı (Türkçe)</Label>
                  <Input
                    id="name-tr"
                    placeholder="Kategori adı"
                    value={name.tr}
                    onChange={(e) => setName({ ...name, tr: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Açıklama (Türkçe)</Label>
                  <div className="react-quill-wrapper" onClick={(e) => {
                    const editor = e.currentTarget.querySelector('.ql-editor');
                    if (editor instanceof HTMLElement) {
                      editor.focus();
                    }
                  }}>
                    <ReactQuill 
                      theme="snow"
                      value={description.tr}
                      onChange={(value) => setDescription({ ...description, tr: value })}
                      className="h-48 mb-12"
                      modules={modules}
                      preserveWhitespace={true}
                    />
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="en" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name-en">Kategori Adı (English)</Label>
                  <Input
                    id="name-en"
                    placeholder="Category name"
                    value={name.en}
                    onChange={(e) => setName({ ...name, en: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Açıklama (English)</Label>
                  <div className="react-quill-wrapper" onClick={(e) => {
                    const editor = e.currentTarget.querySelector('.ql-editor');
                    if (editor instanceof HTMLElement) {
                      editor.focus();
                    }
                  }}>
                    <ReactQuill 
                      theme="snow"
                      value={description.en}
                      onChange={(value) => setDescription({ ...description, en: value })}
                      className="h-48 mb-12"
                      modules={modules}
                      preserveWhitespace={true}
                    />
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="ar" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name-ar">Kategori Adı (العربية)</Label>
                  <Input
                    id="name-ar"
                    placeholder="اسم الفئة"
                    value={name.ar}
                    onChange={(e) => setName({ ...name, ar: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Açıklama (العربية)</Label>
                  <div className="react-quill-wrapper" onClick={(e) => {
                    const editor = e.currentTarget.querySelector('.ql-editor');
                    if (editor instanceof HTMLElement) {
                      editor.focus();
                    }
                  }}>
                    <ReactQuill 
                      theme="snow"
                      value={description.ar}
                      onChange={(value) => setDescription({ ...description, ar: value })}
                      className="h-48 mb-12"
                      modules={modules}
                      preserveWhitespace={true}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            
            <div className="space-y-2">
              <Label>İkon</Label>
              <IconSearchSelect value={icon} onChange={setIcon} />
              {icon && <div className="mt-2 flex items-center gap-2 text-muted-foreground"><span>Seçili:</span> {icon && (() => { const Icon = lucideIcons[icon]; return <Icon className="h-5 w-5" />; })()} <span>{icon}</span></div>}
            </div>
          </div>
          
          <div>
            <Card>
              <CardContent className="p-6">
                <Label>Kategori Görseli</Label>
                <div className="space-y-2">
                  <Label htmlFor="image">Resim</Label>
                  <ImageUpload
                    currentImage={currentImageUrl}
                    onChange={(file) => setImage(file)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="flex justify-end gap-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleCancel}
          >
            İptal
          </Button>
          <Button type="submit" disabled={isSubmitting || !icon || !name.tr.trim()}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Kaydediliyor...
              </>
            ) : "Kaydet"}
          </Button>
        </div>
      </form>
    </ContentLayout>
  );
} 